import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import {
  Lightbulb, ArrowLeft, CheckCircle2, XCircle, Merge, ArrowUpCircle,
  ChevronDown, ChevronUp, Filter, Star, AlertTriangle, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import type { IdeationRun, IdeationCandidate, Product, Offer } from "@shared/schema";
import { useState, useMemo } from "react";

const CANDIDATE_STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  draft: { label: "Under Review", color: "text-muted-foreground", icon: Lightbulb },
  kept: { label: "Kept", color: "text-green-600", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "text-red-500", icon: XCircle },
  merged: { label: "Merged", color: "text-blue-500", icon: Merge },
  promoted: { label: "Promoted", color: "text-primary", icon: ArrowUpCircle },
};

function ConfidenceBadge({ score }: { score: number | null }) {
  if (!score) return null;
  const color = score >= 85 ? "text-green-600 bg-green-50 border-green-200" :
    score >= 70 ? "text-amber-600 bg-amber-50 border-amber-200" :
    "text-muted-foreground bg-muted border-border";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${color}`} data-testid="badge-confidence">
      <Star className="h-3 w-3" />
      {score}%
    </span>
  );
}

function JsonList({ items, label }: { items: unknown; label: string }) {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return null;
  return (
    <div>
      <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-1.5">{label}</h4>
      <ul className="space-y-1">
        {arr.map((item: string, i: number) => (
          <li key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
            <span className="text-muted-foreground mt-0.5 shrink-0">-</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CandidateCard({
  candidate,
  allCandidates,
  brandId,
  runId,
}: {
  candidate: IdeationCandidate;
  allCandidates: IdeationCandidate[];
  brandId: string;
  runId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const { toast } = useToast();
  const statusCfg = CANDIDATE_STATUS_CONFIG[candidate.status] || CANDIDATE_STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  const updateMutation = useMutation({
    mutationFn: async (data: { status: string }) => {
      const res = await apiRequest("PATCH", `/api/ideation-candidates/${candidate.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId, "candidates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId] });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/ideation-candidates/${candidate.id}/merge`, {
        mergeTargetCandidateId: mergeTarget,
      });
      return res.json();
    },
    onSuccess: () => {
      setMergeOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId, "candidates"] });
      toast({ title: "Candidate merged" });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/ideation-candidates/${candidate.id}/promote`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId, "candidates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "personas"] });
      toast({ title: "Promoted to full persona" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const mergeTargets = allCandidates.filter(
    (c) => c.id !== candidate.id && c.status !== "rejected" && c.status !== "merged"
  );

  return (
    <>
      <div
        className={`rounded-xl border bg-card transition-all ${
          candidate.status === "rejected" ? "opacity-50" : ""
        } ${candidate.status === "promoted" ? "ring-1 ring-primary/30" : ""}`}
        data-testid={`card-candidate-${candidate.id}`}
      >
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2.5">
              {candidate.rankOrder && (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold" data-testid={`text-rank-${candidate.id}`}>
                  {candidate.rankOrder}
                </span>
              )}
              <div>
                <h3 className="font-semibold text-sm" data-testid={`text-candidate-name-${candidate.id}`}>{candidate.name}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusIcon className={`h-3.5 w-3.5 ${statusCfg.color}`} />
                  <span className={`text-[11px] font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ConfidenceBadge score={candidate.confidenceScore} />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setExpanded(!expanded)}
                data-testid={`button-expand-${candidate.id}`}
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mb-3" data-testid={`text-candidate-summary-${candidate.id}`}>
            {candidate.summary}
          </p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            {candidate.worldview && (
              <div>
                <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-0.5">Worldview</h4>
                <p className="text-xs text-foreground/80">{candidate.worldview}</p>
              </div>
            )}
            {candidate.primaryPain && (
              <div>
                <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-0.5">Primary Pain</h4>
                <p className="text-xs text-foreground/80">{candidate.primaryPain}</p>
              </div>
            )}
          </div>

          {expanded && (
            <div className="space-y-4 pt-3 border-t">
              <div className="grid grid-cols-2 gap-3">
                {candidate.desiredOutcome && (
                  <div>
                    <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-0.5">Desired Outcome</h4>
                    <p className="text-xs text-foreground/80">{candidate.desiredOutcome}</p>
                  </div>
                )}
                {candidate.deepFear && (
                  <div>
                    <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-0.5">Deep Fear</h4>
                    <p className="text-xs text-foreground/80">{candidate.deepFear}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <JsonList items={candidate.coreBeliefsJson} label="Core Beliefs" />
                <JsonList items={candidate.objectionsJson} label="Objections" />
                <JsonList items={candidate.trustTriggersJson} label="Trust Triggers" />
                <JsonList items={candidate.purchaseDriversJson} label="Purchase Drivers" />
                <JsonList items={candidate.emotionalHooksJson} label="Emotional Hooks" />
                <JsonList items={candidate.languagePatternsJson} label="Language Patterns" />
              </div>

              {candidate.whyThisPersonaMatters && (
                <div className="bg-primary/5 rounded-lg p-3">
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-primary mb-1">Why This Persona Matters</h4>
                  <p className="text-xs text-foreground/80">{candidate.whyThisPersonaMatters}</p>
                </div>
              )}

              {candidate.differentiationReason && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground mb-0.5">Differentiation</h4>
                  <p className="text-xs text-foreground/80">{candidate.differentiationReason}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <JsonList items={candidate.messagingImplicationsJson} label="Messaging Implications" />
                <JsonList items={candidate.proofImplicationsJson} label="Proof Implications" />
                <JsonList items={candidate.creativeTreatmentImplicationsJson} label="Creative Treatment" />
              </div>
            </div>
          )}

          {candidate.status !== "promoted" && candidate.status !== "rejected" && candidate.status !== "merged" && (
            <div className="flex items-center gap-2 mt-4 pt-3 border-t">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-xs h-7"
                onClick={() => updateMutation.mutate({ status: "kept" })}
                disabled={updateMutation.isPending || candidate.status === "kept"}
                data-testid={`button-keep-${candidate.id}`}
              >
                <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
                Keep
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-xs h-7"
                onClick={() => updateMutation.mutate({ status: "rejected" })}
                disabled={updateMutation.isPending}
                data-testid={`button-reject-${candidate.id}`}
              >
                <XCircle className="h-3 w-3 mr-1 text-red-500" />
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-xs h-7"
                onClick={() => setMergeOpen(true)}
                disabled={updateMutation.isPending}
                data-testid={`button-merge-${candidate.id}`}
              >
                <Merge className="h-3 w-3 mr-1 text-blue-500" />
                Merge
              </Button>
              {(candidate.status === "kept" || candidate.status === "draft") && (
                <Button
                  size="sm"
                  className="rounded-full text-xs h-7 ml-auto"
                  onClick={() => promoteMutation.mutate()}
                  disabled={promoteMutation.isPending}
                  data-testid={`button-promote-${candidate.id}`}
                >
                  <ArrowUpCircle className="h-3 w-3 mr-1" />
                  {promoteMutation.isPending ? "Promoting..." : "Promote to Persona"}
                </Button>
              )}
            </div>
          )}

          {candidate.status === "promoted" && candidate.promotedPersonaId && (
            <div className="mt-4 pt-3 border-t">
              <Link href={`/brands/${brandId}/personas/${candidate.promotedPersonaId}`}>
                <Button size="sm" variant="outline" className="rounded-full text-xs h-7" data-testid={`link-promoted-persona-${candidate.id}`}>
                  <ArrowUpCircle className="h-3 w-3 mr-1 text-primary" />
                  View Promoted Persona
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Candidate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Select which candidate "{candidate.name}" should be merged into:
          </p>
          <SearchableSelect
            value={mergeTarget}
            onValueChange={setMergeTarget}
            placeholder="Select target candidate"
            testId="select-merge-target"
            options={mergeTargets.map((c) => ({ value: c.id, label: c.name }))}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button
              onClick={() => mergeMutation.mutate()}
              disabled={!mergeTarget || mergeMutation.isPending}
              data-testid="button-confirm-merge"
            >
              Confirm Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function IdeatorDetailPage() {
  const { brandId, runId } = useParams<{ brandId: string; runId: string }>();
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("rank");
  const { toast } = useToast();

  const { data: run, isLoading: runLoading } = useQuery<IdeationRun>({
    queryKey: ["/api/ideation-runs", runId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!runId,
  });

  const { data: candidates, isLoading: candidatesLoading } = useQuery<IdeationCandidate[]>({
    queryKey: ["/api/ideation-runs", runId, "candidates"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!runId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/brands", brandId, "products"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId && !!run?.productId,
  });

  const { data: offers } = useQuery<Offer[]>({
    queryKey: ["/api/brands", brandId, "offers"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId && !!run?.offerId,
  });

  const promotedItemLabel = run?.offerId
    ? offers?.find((o) => o.id === run.offerId)?.name
    : run?.productId
    ? products?.find((p) => p.id === run.productId)?.name
    : undefined;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/ideation-runs/${runId}/generate`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId] });
      queryClient.invalidateQueries({ queryKey: ["/api/ideation-runs", runId, "candidates"] });
      toast({ title: "Candidates generated" });
    },
    onError: (e: Error) => {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    },
  });

  const filteredCandidates = useMemo(() => {
    if (!candidates) return [];
    let result = candidates;
    if (statusFilter !== "all") {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (sortBy === "confidence") {
      result = [...result].sort((a, b) => (b.confidenceScore ?? 0) - (a.confidenceScore ?? 0));
    }
    return result;
  }, [candidates, statusFilter, sortBy]);

  if (runLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <p className="text-muted-foreground">Run not found</p>
      </div>
    );
  }

  const keptCount = candidates?.filter((c) => c.status === "kept").length ?? 0;
  const rejectedCount = candidates?.filter((c) => c.status === "rejected").length ?? 0;
  const promotedCount = candidates?.filter((c) => c.status === "promoted").length ?? 0;
  const draftCount = candidates?.filter((c) => c.status === "draft").length ?? 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href={`/brands/${brandId}/ideator`}>
          <Button variant="ghost" size="icon" className="rounded-full" data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-run-name">{run.name}</h1>
          <p className="text-sm text-muted-foreground">{run.briefDescription}</p>
        </div>
        {run.status === "draft" && (
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="rounded-full"
            data-testid="button-generate-candidates"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {generateMutation.isPending ? "Generating..." : "Generate Candidates"}
          </Button>
        )}
      </div>

      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Status:</span>
            <Badge variant={run.status === "complete" ? "default" : "outline"} data-testid="badge-run-status">
              {run.status}
            </Badge>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span data-testid="text-total-count">{candidates?.length ?? 0} candidates</span>
            {draftCount > 0 && <span>{draftCount} reviewing</span>}
            {keptCount > 0 && <span className="text-green-600">{keptCount} kept</span>}
            {rejectedCount > 0 && <span className="text-red-500">{rejectedCount} rejected</span>}
            {promotedCount > 0 && <span className="text-primary">{promotedCount} promoted</span>}
          </div>
          {promotedItemLabel && (
            <Badge variant="outline" className="text-[10px]" data-testid="badge-promoted-item">
              {run.offerId ? "Offer" : "Product"}: {promotedItemLabel}
            </Badge>
          )}
          {run.category && <Badge variant="outline" className="text-[10px]">{run.category}</Badge>}
        </div>
      </div>

      {candidates && candidates.length > 0 && (
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="select-candidate-status-filter">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Under Review</SelectItem>
              <SelectItem value="kept">Kept</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="merged">Merged</SelectItem>
              <SelectItem value="promoted">Promoted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px]" data-testid="select-candidate-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rank">By Rank</SelectItem>
              <SelectItem value="confidence">By Confidence</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {candidatesLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      ) : filteredCandidates.length === 0 && candidates?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted mb-4">
            <AlertTriangle className="h-7 w-7 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-1">No candidates yet</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {run.status === "draft"
              ? "Click 'Generate Candidates' to create persona hypotheses from your brief."
              : run.status === "processing"
              ? "Generation in progress..."
              : "No candidates were generated for this run."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCandidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              allCandidates={candidates || []}
              brandId={brandId}
              runId={runId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
